/**
 * <ExtraHoursFlow> — « Réserver un cours en plus », de bout en bout (template).
 *
 * Trois étapes, comme le legacy (`ParentExtraHoursModal.js`) : les paramètres de la demande, puis
 * — selon le format — les créneaux préférés d'une semaine type ou les dates exactes, puis la
 * validation de l'achat. La demande ne part QUE de cette dernière, par « Confirmer l'achat » ;
 * « Retour » y ramène au formulaire sans rien envoyer. C'est un achat, pas un changement de
 * planning, et l'étape le dit avec son prix avant que le parent s'engage. Quand rien ne se facture
 * (`recapKind="request"`), elle ne parle plus d'achat : « Votre demande », « Confirmer la demande ».

 *
 *  - **partagé** : le parent coche toutes ses disponibilités ; plus il y en a, plus le routage
 *    automatique a de chances de trouver un cours. Seuls les jours atteignables avant l'échéance
 *    sont proposés — l'écran ne passe que ceux-là.
 *  - **individuel** : le parent donne une date exacte par heure demandée. Le routage automatique
 *    est court-circuité, l'équipe planifie à la main — c'est pourquoi « Pour quand ? » disparaît.
 *
 * Contrôlé : l'étape courante, le brouillon, l'envoi en cours, l'erreur et le résultat arrivent en
 * props ; la machine à états vit côté écran. Le gabarit ne décide que d'une chose, et elle est
 * structurelle : une étape incomplète n'ouvre pas la suivante. Les seuils (1 à 3 heures, 3 jours
 * de délai, dates en double…) sont des règles métier : elles arrivent écrites dans `errors`.
 *
 * Toute la copie du geste est ICI. Le parent est vouvoyé, son enfant est à la 3ᵉ personne.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { ExtraHoursDatesStep } from "../../organisms/ExtraHoursDatesStep"
import { ExtraHoursParametersStep } from "../../organisms/ExtraHoursParametersStep"
import { ExtraHoursRecapStep } from "../../organisms/ExtraHoursRecapStep"
import { TimeSlotGrid } from "../../organisms/TimeSlotGrid"
import {
    type UIExtraHoursBilling,
    type UIExtraHoursDeadlineOption,
    type UIExtraHoursDraft,
    type UIExtraHoursFormatOption,
    type UIExtraHoursRecapKind,
    type UIExtraHoursStep,
    type UISubject,
    type UITimeSlotDay,
} from "../../../ports/primary/ui/types"
import { ParentFlowModal } from "../ParentFlowModal"

/**
 * Les étapes du geste. `slots` et `dates` sont la MÊME deuxième étape, dans ses deux branches :
 * l'écran choisit celle que le format impose. Le nom des étapes vit dans `ui/types`, parce que
 * l'adaptateur s'en sert aussi (`extraHoursStepComplete`).
 */
export type ExtraHoursStep = UIExtraHoursStep

const TOTAL_STEPS = 3

export type ExtraHoursFlowProps = {
    visible: boolean
    onClose: () => void
    step: ExtraHoursStep
    /** Prénom de l'enfant — la ligne de rappel de la feuille. */
    childFirstname: string

    /** Le brouillon de la demande, possédé par l'écran. */
    draft: UIExtraHoursDraft
    onChange: (next: UIExtraHoursDraft) => void

    // ── Catalogues (données de référence, pas des champs du brouillon) ──
    hourOptions: number[]
    formatOptions: UIExtraHoursFormatOption[]
    deadlineOptions: UIExtraHoursDeadlineOption[]
    /** Les matières des objectifs de l'élève — proposées d'abord. */
    subjects: UISubject[]
    /** Les autres matières de sa classe, derrière « Voir d'autres matières ». */
    otherSubjects?: UISubject[]
    /** Les jours atteignables avant l'échéance choisie, avec leurs créneaux. */
    days?: UITimeSlotDay[]

    // ── Bornes et montants, calculés par l'écran ──
    /** Première date choisissable (échéance précise et dates exactes). */
    minDate?: Date
    /** Dernière date choisissable. */
    maxDate?: Date
    /** « Les heures sont exprimées en heure de Paris. » — seulement si le fuseau diffère. */
    timezoneNotice?: string
    /** Le prix TTC de ce qui se facture, déjà écrit — « 28 € ». */
    totalLabel: string
    /** Quand la demande se facture. */
    billing: UIExtraHoursBilling
    /** Ce que l'heure du mois de l'offre Autonomie fait à la demande, déjà écrit — absent hors Autonomie. */
    autonomyNote?: string
    /** Ce que la dernière étape fait valider, décidé par l'adaptateur. Défaut `purchase`. */
    recapKind?: UIExtraHoursRecapKind

    // ── Navigation et envoi ──
    onContinue: () => void
    onBack?: () => void
    onSubmit?: () => void
    submitting?: boolean
    /** Ce que le serveur a refusé. */
    submitError?: string
    /** Les règles locales non tenues, écrites par le view-model. Bloquent l'envoi. */
    errors?: string[]
    /**
     * « Continuer » est éteint — le verdict de l'écran (`extraHoursStepComplete`). Absent, le
     * gabarit retombe sur sa propre lecture du brouillon, pour qu'une story reste jouable sans
     * adaptateur. Les deux disent la même chose ; l'adaptateur fait foi.
     */
    continueDisabled?: boolean
    testID?: string
}

export function ExtraHoursFlow(props: ExtraHoursFlowProps) {
    const {
        visible,
        onClose,
        step,
        childFirstname,
        draft,
        onChange,
        hourOptions,
        formatOptions,
        deadlineOptions,
        subjects,
        otherSubjects = [],
        days = [],
        minDate,
        maxDate,
        timezoneNotice,
        totalLabel,
        billing,
        autonomyNote,
        recapKind = "purchase",
        onContinue,
        onBack,
        onSubmit,
        submitting = false,
        submitError,
        errors = [],
        continueDisabled,
        testID,
    } = props

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />
    const close = <Button label="Fermer" variant="outline" onPress={onClose} />

    // Le verdict de l'écran gagne ; le repli n'existe que pour les stories. Les deux lectures se
    // suivent au mot près — leur version d'autorité est `extraHoursStepComplete` (adaptateur).
    const continueButton = (fallbackComplete: boolean) => (
        <Stack gap="2">
            <Button label="Continuer" onPress={onContinue} disabled={continueDisabled ?? !fallbackComplete} fullWidth />
            {cancel}
        </Stack>
    )

    /** Une échéance précise n'est un choix qu'une fois la date posée — l'individuel n'en a pas. */
    const customDeadlinePending =
        draft.format !== "individual" &&
        deadlineOptions.some((o) => o.custom === true && o.value === draft.deadlineType) &&
        !draft.customDeadline

    const parametersComplete = draft.subjectId != null && !customDeadlinePending
    const datesComplete =
        draft.requestedDates.length === draft.hours && draft.requestedDates.every((v) => v != null && v !== "")

    const content = (() => {
        switch (step) {
            case "slots":
                return {
                    stepTitle: "Créneaux préférés",
                    stepIndex: 2,
                    footer: continueButton(draft.slotIds.length > 0),
                    body: (
                        <Stack gap="4">
                            {/* Les deux choses qu'on ne devine pas : pourquoi cocher large, et
                                pourquoi des jours manquent. */}
                            <Text size="text-sm" color="text-muted">
                                Cochez tous les créneaux où votre enfant est disponible — plus il y en a, plus vite on
                                lui trouvera un cours. Seuls les jours atteignables avant votre échéance sont affichés.
                            </Text>
                            <TimeSlotGrid
                                days={days}
                                selectedIds={draft.slotIds}
                                onToggle={(slotId) =>
                                    onChange({
                                        ...draft,
                                        slotIds: draft.slotIds.includes(slotId)
                                            ? draft.slotIds.filter((id) => id !== slotId)
                                            : [...draft.slotIds, slotId],
                                    })
                                }
                                emptyMessage="Aucun jour n'est atteignable avant cette échéance."
                            />
                        </Stack>
                    ),
                }

            case "dates":
                return {
                    stepTitle: "Dates et heures souhaitées",
                    stepIndex: 2,
                    footer: continueButton(datesComplete),
                    body: (
                        <ExtraHoursDatesStep
                            draft={draft}
                            onChange={onChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            timezoneNotice={timezoneNotice}
                        />
                    ),
                }

            case "recap":
                return {
                    stepTitle: recapKind === "request" ? "Votre demande" : "Votre achat",
                    stepIndex: 3,
                    footer: (
                        <Stack gap="2">
                            {/* Ce qui refuse l'envoi se lit contre le bouton, pas au fond du corps
                                qui défile : sinon le parent voit un bouton éteint sans sa raison.
                                Une raison par ligne — `<Banner>` écrit son message dans un
                                `<Text>`, où une pile de vues n'a pas sa place. */}
                            {errors.length > 0 && <AlertCard severity="alert" message={errors.join("\n")} />}
                            {submitError != null && submitError !== "" && (
                                <AlertCard severity="alert" message={submitError} />
                            )}
                            <Button
                                label={recapKind === "request" ? "Confirmer la demande" : "Confirmer l'achat"}
                                onPress={onSubmit}
                                loading={submitting}
                                disabled={submitting || errors.length > 0}
                                fullWidth
                            />
                            {/* « Retour » plutôt qu'« Annuler » : devant un prix, le parent qui hésite
                                veut revoir sa demande, pas la jeter. La croix de la feuille ferme
                                toujours, et l'étape garde deux boutons. */}
                            <Button label="Retour" variant="outline" onPress={onBack} disabled={submitting} />
                        </Stack>
                    ),
                    body: (
                        <ExtraHoursRecapStep
                            draft={draft}
                            childFirstname={childFirstname}
                            formatOptions={formatOptions}
                            deadlineOptions={deadlineOptions}
                            subjects={[...subjects, ...otherSubjects]}
                            days={days}
                            totalLabel={totalLabel}
                            billing={billing}
                            autonomyNote={autonomyNote}
                            recapKind={recapKind}
                        />
                    ),
                }

            case "done":
                return {
                    stepTitle: "Demande envoyée",
                    stepIndex: 3,
                    footer: close,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            message="Nous revenons vers vous dès que la demande a été traitée."
                        />
                    ),
                }

            case "parameters":
            default:
                return {
                    stepTitle: "Votre demande",
                    stepIndex: 1,
                    footer: continueButton(parametersComplete),
                    body: (
                        <ExtraHoursParametersStep
                            draft={draft}
                            onChange={onChange}
                            hourOptions={hourOptions}
                            formatOptions={formatOptions}
                            deadlineOptions={deadlineOptions}
                            subjects={subjects}
                            otherSubjects={otherSubjects}
                            minDeadlineDate={minDate}
                            maxDeadlineDate={maxDate}
                        />
                    ),
                }
        }
    })()

    return (
        <ParentFlowModal
            visible={visible}
            onClose={onClose}
            title="Réserver un cours en plus"
            stepTitle={content.stepTitle}
            step={content.stepIndex}
            totalSteps={TOTAL_STEPS}
            onBack={step === "parameters" || step === "done" ? undefined : onBack}
            context={`pour ${childFirstname}`}
            footer={content.footer}
            testID={testID}
        >
            {content.body}
        </ParentFlowModal>
    )
}
